import { useQuery } from '@tanstack/react-query';
import { Select } from 'antd';

import { CenterFlex, Flex, VFlex } from '@/components/base';
import Transition from '@/components/Common/Transition';
import FeedbackVolumeDisplay from '@/components/Display/FeedbackVolumeDisplay';
import HeroCountDisplay from '@/components/Display/HeroCountDisplay';
import { MainText } from '@/components/Typography/NamedTypo';
import Typo from '@/components/Typography/Typo';
import queryKeys from '@/configs/react-query-keys';
import type { CHART_FREQUENCY } from '@/entities/chart/model/constants';
import type { ApiError } from '@/error-handler/ApiError';
import { ChartFrequencyMap } from '@/features/(charts)/chart-renderer/model/chart-maps';
import { useSearchDetailContext } from '@/features/GlobalSearch/context/SearchDetailContext';
import useAppInit from '@/hooks/domain/App/useAppInit';
import service from '@/services/index';

import { DownloadButton } from '../SearchContentStyles';

type Props = {
  alias: string;
};

const SearchFeedbackTrend = (props: Props) => {
  const appInit = useAppInit();

  const { frequency, updateFrequency } = useSearchDetailContext();

  const {
    data: trend,
    isLoading: trendLoading,
    refetch,
    isError,
    error,
  } = useQuery<StatisticDataT, ApiError>({
    queryFn: async () => {
      return await service.globalSearch.fetchSearchFeedbackTrend(props.alias);
    },
    queryKey: [
      queryKeys.search.SEARCH_DETAIL,
      queryKeys.search.SEARCH_RESULT_FEEDBACK_TREND,
      props.alias,
    ],
    enabled: appInit,
  });

  const onFrequencyChangeHandler = async (value: CHART_FREQUENCY) => {
    updateFrequency(value);
    await service.globalSearch.updateSearchOptions(props.alias, {
      trend_frequency: value,
    });
    refetch();
  };

  const total = trend?.total || 0;

  return (
    <>
      <Flex j="space-between">
        <Flex a="center" g={12}>
          <Typo type="label0" weight="m">
            Feedback volume
          </Typo>
          <Select
            style={{ width: 120 }}
            value={frequency}
            options={ChartFrequencyMap.toArray()}
            onChange={onFrequencyChangeHandler}
            size="small"
          />
        </Flex>
        <DownloadButton />
      </Flex>
      <Transition
        style={{ height: 160 }}
        status={!isError}
        fallback={
          <CenterFlex f={1} height="100%">
            <MainText dim>{error?.message}</MainText>
          </CenterFlex>
        }
      >
        <Flex g={24} f={1}>
          <FeedbackVolumeDisplay
            feedbackTrend={trend}
            isLoading={trendLoading}
            frequency={frequency}
          />
          <VFlex a="flex-end">
            <HeroCountDisplay count={total} changes={null} />
            <Typo type="label0" weight="m" className="text-gray-800">
              Total feedback
            </Typo>
          </VFlex>
        </Flex>
      </Transition>
    </>
  );
};

export default SearchFeedbackTrend;
